/**
 * Hľadanie zákazníka — obrazovka pre technika v teréne.
 *
 * Odpovedá na otázku „kde je tento človek pripojený": na ktorom stĺpiku, v
 * ktorej kazete, na ktorom splittri a výstupe a akú farbu má jeho vlákno.
 * Hľadá sa vo všetkých projektoch, na ktoré má používateľ právo — technik pri
 * poruche nevie, či je zákazník v Ohaji alebo v Melku.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  searchCustomers,
  NajdenyZakaznik,
  VysledokHladaniaZakaznikov,
} from '../../services/searchService';
import { farbaVlakna, popisZdroja, prejdiNaZakaznika } from '../../utils/customerSearch';
import { poleTypeIcon, poleTypeLabel } from '../../utils/poleLabels';
import './CustomerSearchView.css';

const MIN_ZNAKOV = 2;
const PAUZA_MS = 350;

/** Farebný štítok vlákna — to, čo technik vidí v kazete. */
const StitokVlakna: React.FC<{ cislo?: number | null; popis?: string }> = ({ cislo, popis }) => {
  const f = farbaVlakna(cislo);
  if (!f) return <span className="zs-neznamy">—</span>;
  return (
    <span className="zs-vlakno" style={{ background: f.hex, color: f.text }}>
      {popis ? `${popis} ` : ''}#{cislo} {f.nazov}
    </span>
  );
};

const Karta: React.FC<{ z: NajdenyZakaznik }> = ({ z }) => {
  const [pracujem, setPracujem] = useState(false);

  const chod = async (otvorKazetu: boolean) => {
    setPracujem(true);
    try {
      await prejdiNaZakaznika(z, { otvorKazetu });
    } finally {
      setPracujem(false);
    }
  };

  const splitterCislo =
    z.splitter && z.splitter.index !== null && z.splitter.index !== undefined
      ? z.splitter.index + 1
      : null;

  return (
    <div className={`zs-karta zs-zdroj-${z.zdroj}`}>
      <div className="zs-hlavicka">
        <div className="zs-meno">{z.meno || 'Bez mena'}</div>
        <span className={`zs-znacka zs-znacka-${z.zdroj}`}>{popisZdroja(z.zdroj)}</span>
      </div>

      <div className="zs-kontakt">
        {z.telefon ? (
          <a className="zs-telefon" href={`tel:${z.telefon.replace(/\s/g, '')}`}>📞 {z.telefon}</a>
        ) : (
          <span className="zs-neznamy">📞 telefón nezadaný</span>
        )}
        {z.adresa && <span className="zs-adresa">🏠 {z.adresa}</span>}
        {z.cislo_zakaznika && <span className="zs-cislo">{z.cislo_zakaznika}</span>}
      </div>

      <div className="zs-mriezka">
        <div className="zs-polozka">
          <div className="zs-popis">Projekt</div>
          <div className="zs-hodnota">{z.projekt.nazov}</div>
        </div>

        <div className="zs-polozka">
          <div className="zs-popis">Stĺpik</div>
          <div className="zs-hodnota">
            {z.stlpik.nazov ? (
              <>
                {poleTypeIcon(z.stlpik.typ)} {z.stlpik.nazov}
                <span className="zs-typ">{poleTypeLabel(z.stlpik.typ)}</span>
              </>
            ) : (
              <span className="zs-neznamy">neznámy</span>
            )}
          </div>
        </div>

        <div className="zs-polozka">
          <div className="zs-popis">Splitter</div>
          <div className="zs-hodnota">
            {splitterCislo ? (
              <>
                č. {splitterCislo}
                {z.splitter?.typ && <span className="zs-typ">{z.splitter.typ}</span>}
              </>
            ) : (
              <span className="zs-neznamy">neznámy</span>
            )}
          </div>
        </div>

        <div className="zs-polozka">
          <div className="zs-popis">Výstup a farba vlákna</div>
          <div className="zs-hodnota">
            {z.vystup ? <StitokVlakna cislo={z.vystup} /> : <span className="zs-neznamy">neznámy</span>}
          </div>
        </div>

        <div className="zs-polozka zs-siroka">
          <div className="zs-popis">Splitter je kŕmený</div>
          <div className="zs-hodnota">
            {z.splitter?.vstupne_vlakno ? (
              <>
                <StitokVlakna cislo={z.splitter.vstupne_vlakno} popis="vlákno" />
                {z.splitter.vstupny_buffer ? (
                  <StitokVlakna cislo={z.splitter.vstupny_buffer} popis="buffer" />
                ) : null}
                {z.splitter.vstupny_kabel && (
                  <span className="zs-kabel">🔌 {z.splitter.vstupny_kabel}</span>
                )}
              </>
            ) : (
              <span className="zs-neznamy">nezadané</span>
            )}
          </div>
        </div>
      </div>

      {z.kazeta?.virtualna && (
        <div className="zs-poznamka">
          Zákaznícka kazeta <b>{z.kazeta.nazov}</b> patrí k stĺpiku <b>{z.stlpik.nazov}</b>.
        </div>
      )}

      {z.zdroj === 'zoznam' && (
        <div className="zs-upozornenie">
          V kazete nie je zapísaný na konkrétnom výstupe — číslo výstupu ani farbu vlákna
          preto neviem. {z.aj_v_kazete ? 'Rovnaké meno sa v niektorej kazete nachádza.' : ''}
        </div>
      )}

      {z.zdroj === 'onu' && z.onu && (
        <div className="zs-poznamka">
          ONU {z.onu.nazov}
          {z.onu.seriove_cislo ? ` · ${z.onu.seriove_cislo}` : ''}
          {z.onu.mac ? ` · ${z.onu.mac}` : ''}
        </div>
      )}

      {(z.osadil || z.kedy) && (
        <div className="zs-osadil">
          {z.osadil && <>Osadil {z.osadil}</>}
          {z.kedy && <> · {new Date(z.kedy).toLocaleDateString('sk-SK')}</>}
        </div>
      )}

      <div className="zs-akcie">
        <button className="zs-btn" disabled={!z.stlpik.id || pracujem} onClick={() => chod(false)}>
          📍 Ukáž na mape
        </button>
        <button
          className="zs-btn zs-btn-hlavne"
          disabled={!z.stlpik.id || pracujem}
          onClick={() => chod(true)}
        >
          🗄️ Otvor kazetu
        </button>
      </div>
    </div>
  );
};

export const CustomerSearchView: React.FC = () => {
  const [dotaz, setDotaz] = useState('');
  const [vysledok, setVysledok] = useState<VysledokHladaniaZakaznikov | null>(null);
  const [hladam, setHladam] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Aby staršia, pomalšia odpoveď neprepísala novšiu
  const poslednyDotaz = useRef('');

  const hladaj = useCallback(async (q: string) => {
    poslednyDotaz.current = q;
    if (q.trim().length < MIN_ZNAKOV) {
      setVysledok(null);
      setChyba(null);
      setHladam(false);
      return;
    }
    setHladam(true);
    setChyba(null);
    try {
      const r = await searchCustomers(q.trim(), 100);
      if (poslednyDotaz.current !== q) return;
      setVysledok(r);
    } catch (e: any) {
      if (poslednyDotaz.current !== q) return;
      setChyba(e?.message || 'Hľadanie zlyhalo.');
      setVysledok(null);
    } finally {
      if (poslednyDotaz.current === q) setHladam(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => hladaj(dotaz), PAUZA_MS);
    return () => clearTimeout(t);
  }, [dotaz, hladaj]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="zs-obrazovka">
      <div className="zs-vyhladavanie">
        <h2>🔎 Nájdi zákazníka</h2>
        <p className="zs-navod">
          Meno, adresa, telefón, číslo zákazníka, názov stĺpika alebo sériové číslo ONU.
          Hľadá sa vo všetkých projektoch, na ktoré máš právo.
        </p>
        <div className="zs-pole">
          <input
            ref={inputRef}
            type="search"
            value={dotaz}
            onChange={(e) => setDotaz(e.target.value)}
            placeholder="napr. Hulík, Ohaj 111, 0900, Stlpik_23"
            autoComplete="off"
            enterKeyHint="search"
          />
          {dotaz && (
            <button className="zs-zmaz" onClick={() => setDotaz('')} aria-label="Vymazať">
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="zs-vysledky">
        {chyba && <div className="zs-chyba">{chyba}</div>}

        {!chyba && dotaz.trim().length > 0 && dotaz.trim().length < MIN_ZNAKOV && (
          <div className="zs-hlaska">Napíš aspoň {MIN_ZNAKOV} znaky.</div>
        )}

        {hladam && <div className="zs-hlaska">Hľadám…</div>}

        {!hladam && !chyba && vysledok && vysledok.total === 0 && (
          <div className="zs-hlaska">
            Pre „{vysledok.query}" som nič nenašiel.
            <div className="zs-hlaska-detail">
              Skús priezvisko bez krstného mena, kus adresy alebo posledné cifry telefónu.
            </div>
          </div>
        )}

        {!hladam && vysledok && vysledok.total > 0 && (
          <>
            <div className="zs-pocet">
              Našiel som {vysledok.total}{' '}
              {vysledok.total === 1 ? 'zákazníka' : vysledok.total < 5 ? 'zákazníkov' : 'zákazníkov'}
              {vysledok.orezane && ` (zobrazujem prvých ${vysledok.results.length})`}
            </div>
            {vysledok.results.map((z, i) => (
              <Karta key={`${z.zdroj}-${z.projekt.id}-${z.stlpik.id}-${z.vystup ?? 'x'}-${i}`} z={z} />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerSearchView;
